import { useState, useRef, useCallback, useEffect } from 'react';
import { X, Video, Loader2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { mediaService } from '../../services/media';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

interface VideoUploaderProps {
  value?: string;
  videoId?: string;
  videoStatus?: 'uploading' | 'processing' | 'ready' | 'failed';
  onChange: (videoUrl: string, mediaId: string, status: 'uploading' | 'processing' | 'ready' | 'failed') => void;
  targetType: string;
  targetId: string;
  className?: string;
  disabled?: boolean;
  maxSize?: number; // in bytes, default 2GB
}

type UploadStage = 'idle' | 'uploading' | 'processing' | 'ready' | 'failed';

export default function VideoUploader({
  value,
  videoId,
  videoStatus,
  onChange,
  targetType,
  targetId,
  className = '',
  disabled = false,
  maxSize = 2 * 1024 * 1024 * 1024, // 2GB
}: VideoUploaderProps) {
  const [stage, setStage] = useState<UploadStage>(videoStatus || 'idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentMediaId, setCurrentMediaId] = useState<string | null>(videoId || null);
  const [videoUrl, setVideoUrl] = useState<string | null>(value || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pollFailCountRef = useRef(0);

  // Poll for status if processing
  useEffect(() => {
    if (stage !== 'processing' || !currentMediaId) return;

    const pollStatus = async () => {
      try {
        const status = await mediaService.getVideoStatus(currentMediaId);
        pollFailCountRef.current = 0;
        setProgress(status.progress);

        if (status.status === 'ready') {
          setStage('ready');
          setVideoUrl(status.url);
          onChange(status.url || '', currentMediaId, 'ready');
        } else if (status.status === 'failed') {
          setStage('failed');
          setError(status.error || 'Video processing failed');
          onChange('', currentMediaId, 'failed');
        }
      } catch (err) {
        console.error('Failed to poll video status:', err);
        pollFailCountRef.current += 1;
        if (pollFailCountRef.current >= 5) {
          setStage('failed');
          setError('Lost connection during processing. Please check your network and try again.');
          onChange('', currentMediaId, 'failed');
        }
      }
    };

    // Poll immediately on mount, then every 3 seconds
    pollStatus();
    const interval = setInterval(pollStatus, 3000);
    return () => clearInterval(interval);
  }, [stage, currentMediaId, onChange]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('video/')) {
      setError('Please select a video file');
      return;
    }

    if (file.size > maxSize) {
      setError(`Video must be less than ${formatSize(maxSize)}`);
      return;
    }

    setError(null);
    setStage('uploading');
    setProgress(0);
    pollFailCountRef.current = 0;

    abortControllerRef.current = new AbortController();

    try {
      const result = await mediaService.uploadVideo(
        file,
        targetType,
        targetId,
        (prog, uploadStage) => {
          setProgress(prog);
          if (uploadStage === 'processing') {
            setStage('processing');
          }
        },
        abortControllerRef.current.signal
      );

      setCurrentMediaId(result.mediaId);
      if (result.status === 'ready') {
        // Duplicate detected - video already processed, skip polling
        setStage('ready');
        onChange('', result.mediaId, 'ready');
      } else {
        setStage('processing');
        onChange('', result.mediaId, 'processing');
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        setStage('idle');
        return;
      }
      setStage('failed');
      setError(err.response?.data?.error || err.message || 'Failed to upload video');
    }
  }, [targetType, targetId, maxSize, onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled || stage === 'uploading' || stage === 'processing') return;

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [disabled, stage, handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && stage !== 'uploading' && stage !== 'processing') {
      setIsDragging(true);
    }
  }, [disabled, stage]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleRemove = useCallback(async () => {
    if (currentMediaId) {
      try {
        await mediaService.deleteVideo(currentMediaId);
      } catch (err) {
        console.error('Failed to delete video:', err);
      }
    }
    setStage('idle');
    setProgress(0);
    setCurrentMediaId(null);
    setVideoUrl(null);
    setError(null);
    onChange('', '', 'ready');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [currentMediaId, onChange]);

  const handleCancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setStage('idle');
    setProgress(0);
    setError(null);
  }, []);

  const handleRetry = useCallback(() => {
    setStage('idle');
    setError(null);
    fileInputRef.current?.click();
  }, []);

  const handleClick = useCallback(() => {
    if (!disabled && stage === 'idle') {
      fileInputRef.current?.click();
    }
  }, [disabled, stage]);

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-matroska"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || stage === 'uploading' || stage === 'processing'}
      />

      {stage === 'ready' && (videoUrl || currentMediaId) ? (
        <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-white">
          <video
            src={currentMediaId ? mediaService.getVideoStreamUrl(currentMediaId) : videoUrl || undefined}
            className="w-full h-48 object-cover"
            controls={false}
          />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 text-center">
            <span className="text-green-600 text-sm font-medium">Video uploaded successfully</span>
          </div>
        </div>
      ) : stage === 'uploading' || stage === 'processing' ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-10 h-10 text-gold animate-spin" />
            <div className="text-center">
              <div className="text-gray-600 font-medium">
                {stage === 'uploading'
                  ? 'Uploading video...'
                  : progress === 0
                    ? 'Queued for processing...'
                    : 'Processing video...'}
              </div>
              <div className="text-gray-500 text-sm mt-1">
                {stage === 'uploading'
                  ? 'Please wait while your video uploads'
                  : progress === 0
                    ? 'Your video is in the queue. You can save and come back later.'
                    : 'This may take a few minutes. You can save and come back later.'}
              </div>
            </div>
            <div className="w-full">
              <div className="flex justify-between text-sm text-gray-500 mb-1">
                <span>{stage === 'uploading' ? 'Uploading' : progress === 0 ? 'Queued' : 'Processing'}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gold h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            {stage === 'uploading' && (
              <button
                type="button"
                onClick={handleCancel}
                className="text-red-600 hover:text-red-500 text-sm"
              >
                Cancel upload
              </button>
            )}
          </div>
        </div>
      ) : stage === 'failed' ? (
        <div className="rounded-xl border border-red-500/50 bg-red-500/10 p-6">
          <div className="flex flex-col items-center gap-4">
            <AlertCircle className="w-10 h-10 text-red-500" />
            <div className="text-center">
              <div className="text-red-600 font-medium">Upload failed</div>
              <div className="text-gray-500 text-sm mt-1">{error}</div>
            </div>
            <button
              type="button"
              onClick={handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-100 rounded-lg text-gray-900 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
            ${isDragging ? 'border-gold bg-gold/10' : 'border-gray-200 hover:border-gray-300'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <div className="flex flex-col items-center gap-2">
            <div className="p-3 bg-gray-100 rounded-full">
              <Video className="w-6 h-6 text-gray-500" />
            </div>
            <div className="text-gray-600 font-medium">
              Drop video here or click to upload
            </div>
            <div className="text-gray-500 text-sm">
              MP4, WebM, MOV, AVI, MKV (max {formatSize(maxSize)})
            </div>
          </div>
        </div>
      )}

      {error && stage !== 'failed' && (
        <p className="mt-2 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}
